import { useState } from "react";
import { Loader2, Check, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AmbassadorsTable({ 
  ambassadors, 
  isLoading, 
  onUpdateStatus,
  formatDate 
}) {
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedAmbassador, setSelectedAmbassador] = useState(null);
  const [customCode, setCustomCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApproveClick = (ambassador) => {
    setSelectedAmbassador(ambassador);
    setCustomCode(ambassador.name.toUpperCase().replace(/\s+/g, ''));
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedAmbassador) return;
    setIsSubmitting(true);
    const result = await onUpdateStatus(selectedAmbassador.id, "approved", customCode || null);
    setIsSubmitting(false);
    if (result?.success) {
      setApproveDialogOpen(false);
      setSelectedAmbassador(null);
      setCustomCode("");
    }
  };

  const handleReject = (ambassadorId) => {
    onUpdateStatus(ambassadorId, "rejected", null);
  };

  const handleReset = (ambassadorId) => {
    onUpdateStatus(ambassadorId, "pending", null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  return (
    <>
      <Table data-testid="ambassadors-table">
        <TableHeader>
          <TableRow className="border-b border-[#222] hover:bg-transparent">
            <TableHead className="text-[#FFD700] font-bold uppercase tracking-wider text-xs">Namn</TableHead>
            <TableHead className="text-[#FFD700] font-bold uppercase tracking-wider text-xs">Stad</TableHead>
            <TableHead className="text-[#FFD700] font-bold uppercase tracking-wider text-xs">Kontakt</TableHead>
            <TableHead className="text-[#FFD700] font-bold uppercase tracking-wider text-xs">Sociala medier</TableHead>
            <TableHead className="text-[#FFD700] font-bold uppercase tracking-wider text-xs">Status & Kod</TableHead>
            <TableHead className="text-[#FFD700] font-bold uppercase tracking-wider text-xs">Åtgärder</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ambassadors.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-gray-500 py-12">
                Inga ambassadör-ansökningar ännu
              </TableCell>
            </TableRow>
          ) : (
            ambassadors.map((amb, index) => (
              <AmbassadorRow 
                key={amb.id}
                ambassador={amb}
                index={index}
                onApproveClick={handleApproveClick}
                onReject={handleReject}
                onReset={handleReset}
              />
            ))
          )}
        </TableBody>
      </Table>

      {/* Ambassador Details - About section */}
      {ambassadors.length > 0 && (
        <div className="mt-6 space-y-4">
          <h3 className="text-[#FFD700] font-bold uppercase tracking-wider text-sm">
            Ansökningar - Detaljer
          </h3>
          {ambassadors.map((amb) => (
            <div key={amb.id} className="bg-[#0A0A0A] border border-[#222] p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">{amb.name}</span>
                <StatusBadge status={amb.status} />
              </div>
              <p className="text-gray-400 text-sm whitespace-pre-wrap">{amb.about}</p>
            </div>
          ))}
        </div>
      )}

      {/* Approve Dialog with Custom Code */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-[#222] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#FFD700] font-heading uppercase">
              Godkänn Ambassadör
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Ange en unik kod för {selectedAmbassador?.name}. Denna kod kommer användare att använda för att köpa biljetter.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block">
              Ambassadör-kod
            </label>
            <Input
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
              placeholder="T.ex. RASMUS"
              className="bg-[#1A1A1A] border-[#333] text-white uppercase font-mono"
              data-testid="custom-code-input"
            />
            <p className="text-gray-500 text-xs mt-2">
              Lämna tomt för att generera en automatisk kod (DUNDER-XXXXXX)
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApproveDialogOpen(false)}
              className="border-[#333] text-gray-400 hover:text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleApproveConfirm}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Godkänn
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AmbassadorRow({ ambassador, index, onApproveClick, onReject, onReset }) {
  const amb = ambassador;
  
  return (
    <TableRow 
      className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]/50" 
      data-testid={`ambassador-row-${index}`}
    >
      <TableCell>
        <div>
          <p className="text-white font-medium">{amb.name}</p>
          <p className="text-gray-500 text-xs">{amb.age} år</p>
        </div>
      </TableCell>
      <TableCell className="text-gray-400">{amb.city}</TableCell>
      <TableCell>
        <div className="text-xs">
          <p className="text-gray-400">{amb.email}</p>
          <p className="text-gray-500">{amb.phone}</p>
        </div>
      </TableCell>
      <TableCell>
        <div className="text-xs">
          {amb.instagram && <p className="text-gray-400">{amb.instagram}</p>}
          {amb.tiktok && <p className="text-gray-500">{amb.tiktok}</p>}
          {!amb.instagram && !amb.tiktok && <span className="text-gray-600">-</span>}
        </div>
      </TableCell>
      <TableCell>
        <div>
          <StatusBadge status={amb.status} />
          {amb.referral_code && (
            <p className="text-sm text-[#FFD700] mt-1 font-mono font-bold">{amb.referral_code}</p>
          )}
        </div>
      </TableCell>
      <TableCell>
        {amb.status === "pending" ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => onApproveClick(amb)}
              className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
              data-testid={`approve-${amb.id}`}
            >
              <Check className="w-4 h-4 mr-1" />
              Godkänn
            </Button>
            <Button
              size="sm"
              onClick={() => onReject(amb.id)}
              className="bg-red-600 hover:bg-red-700 text-white h-8 px-2"
              data-testid={`reject-${amb.id}`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReset(amb.id)}
            className="border-[#333] text-gray-400 hover:text-white h-8 px-2 text-xs"
          >
            Återställ
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function StatusBadge({ status }) {
  switch (status) {
    case "approved":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded">
          <Check className="w-3 h-3" /> Godkänd
        </span>
      );
    case "rejected":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-900/50 text-red-400 text-xs rounded">
          <X className="w-3 h-3" /> Nekad
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-900/50 text-yellow-400 text-xs rounded">
          <Clock className="w-3 h-3" /> Väntar
        </span>
      );
  }
}
